/* eslint-disable no-underscore-dangle */
import io from 'socket.io-client';
import { getToken } from '@app/config/apollo/helpers';
import { SOCKET_EVENTS } from '@app/constants/chatConstants';

/**
 * Chat Socket Service. Manages:
 * - Socket.IO connection lifecycle (connect, disconnect, reconnect)
 * - Room management (conversations, groups)
 * - Message sending (1:1 and group)
 * - Event subscriptions
 * - Typing indicators with auto-stop timers
 * - Connection status tracking
 */
class ChatSocketService {
  constructor() {
    // Socket state
    this.socket = null;
    this.subscriptions = new Map();
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.connectionPromise = null;
    this._connectionStatus = 'disconnected';
    this.statusListeners = new Set();

    // Room state
    this.currentConversationId = null;
    this.currentGroupChatId = null;

    // Bulk-subscribed room IDs (persisted across reconnects)
    this._subscribedConversationIds = [];
    this._subscribedEventIds = [];

    // Typing timers
    this.typingTimers = new Map();
  }

  // ===========================================================
  // CONNECTION LIFECYCLE
  // ===========================================================

  connect() {
    if (this.socket?.connected) {
      return Promise.resolve(this.socket);
    }

    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.isConnecting = true;

    this.connectionPromise = new Promise((resolve) => {
      const token = getToken()();
      if (!token) {
        console.warn('No auth token found, delaying socket connection');
        this.isConnecting = false;
        this.connectionPromise = null;

        setTimeout(() => {
          this.connect().then(resolve).catch(() => resolve(null));
        }, 2000);
        return;
      }

      try {
        this.socket = io(process.env.NEXT_PUBLIC_SOCKET_URL || 'http://localhost:4000', {
          auth: { token },
          transports: ['websocket', 'polling'],
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          timeout: 20000,
          forceNew: false,
        });

        this._setupListeners(resolve);
      } catch (error) {
        console.error('Failed to create socket connection:', error.message);
        this.isConnecting = false;
        this.connectionPromise = null;
        resolve(null);
      }
    }).catch((error) => {
      console.error('Socket connection error:', error.message);
      return null;
    });

    return this.connectionPromise;
  }

  _setupListeners(resolve) {
    if (!this.socket) return;

    this.socket.on(SOCKET_EVENTS.CONNECT, () => {
      this.reconnectAttempts = 0;
      this.isConnecting = false;
      this.connectionPromise = null;
      this._updateConnectionStatus('connected');

      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket] Connected, socket id:', this.socket.id);
      }

      this.emit('user.online', { timestamp: Date.now() });

      // Auto-rejoin active rooms after reconnect
      this._rejoinActiveRooms();

      resolve(this.socket);
    });

    this.socket.on(SOCKET_EVENTS.DISCONNECT, (reason) => {
      this._updateConnectionStatus('disconnected');

      // Clear room state on disconnect
      this.currentConversationId = null;
      this.currentGroupChatId = null;

      if (reason === 'io client disconnect') return;

      if (reason === 'forced close') {
        console.error('Socket forced close detected');
      }

      this.scheduleReconnect();
    });

    this.socket.on('connect_error', (error) => {
      console.warn('Socket connection error:', error.message || error);
      this._updateConnectionStatus('connecting');
      this.reconnectAttempts += 1;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.isConnecting = false;
        this.connectionPromise = null;
        this._updateConnectionStatus('disconnected');
        resolve(null);
      }
    });

    this.socket.on(SOCKET_EVENTS.ERROR, (error) => {
      console.error('Socket error:', error.message || error);
    });

    this.socket.on(SOCKET_EVENTS.CONNECT_ERROR, (error) => {
      console.error('Socket authentication error:', error.message || error);
      this.disconnect();
      this.isConnecting = false;
      this.connectionPromise = null;
      this._updateConnectionStatus('disconnected');
      resolve(null);
    });

    this.socket.on(SOCKET_EVENTS.RECONNECT_ERROR, () => {
      this._updateConnectionStatus('connecting');
    });

    this.socket.on(SOCKET_EVENTS.RECONNECT_FAILED, () => {
      console.error('Socket reconnection failed permanently');
      this._updateConnectionStatus('disconnected');
    });

    this.socket.on(SOCKET_EVENTS.RECONNECTING, () => {
      this._updateConnectionStatus('connecting');
    });

    this.socket.on(SOCKET_EVENTS.RECONNECT, () => {
      this._updateConnectionStatus('connected');
    });
  }

  scheduleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = this.reconnectDelay * (2 ** this.reconnectAttempts);
      this._updateConnectionStatus('connecting');

      setTimeout(() => {
        this.connect().catch(() => {});
      }, delay);
    } else {
      this._updateConnectionStatus('disconnected');
    }
  }

  disconnect() {
    if (this.socket) {
      this.subscriptions.forEach((callback, event) => {
        this.socket.off(event, callback);
      });
      this.subscriptions.clear();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.connectionPromise = null;
    this.currentConversationId = null;
    this.currentGroupChatId = null;
    this._subscribedConversationIds = [];
    this._subscribedEventIds = [];
    this._updateConnectionStatus('disconnected');
  }

  reconnect() {
    this.cleanup();
    this.disconnect();
    setTimeout(() => {
      this.connect().catch(() => {});
    }, 1000);
  }

  // ===========================================================
  // CONNECTION STATUS
  // ===========================================================

  _updateConnectionStatus(newStatus) {
    if (this._connectionStatus !== newStatus) {
      this._connectionStatus = newStatus;

      this.statusListeners.forEach((callback) => {
        try {
          callback(newStatus);
        } catch (error) {
          console.error('Error in status listener:', error);
        }
      });
    }
  }

  onStatusChange(callback) {
    this.statusListeners.add(callback);
    return () => this.statusListeners.delete(callback);
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  getConnectionState() {
    return this._connectionStatus;
  }

  // ===========================================================
  // EVENT SUBSCRIPTION (for incoming socket events)
  // ===========================================================

  subscribe(event, callback) {
    if (!this.socket?.connected) {
      return false;
    }

    if (this.subscriptions.has(event)) {
      return true;
    }

    try {
      this.socket.on(event, callback);
      this.subscriptions.set(event, callback);
      return true;
    } catch (error) {
      console.error(`Error subscribing to ${event}:`, error);
      return false;
    }
  }

  unsubscribe(event) {
    if (!this.socket) return false;

    const callback = this.subscriptions.get(event);
    if (callback) {
      this.socket.off(event, callback);
      this.subscriptions.delete(event);
      return true;
    }
    return false;
  }

  // ===========================================================
  // ROOM SUBSCRIPTIONS (bulk, after list loads)
  // ===========================================================

  async subscribeConversations(conversationIds = []) {
    if (!Array.isArray(conversationIds) || conversationIds.length === 0) return false;

    // Store for re-subscription on reconnect
    this._subscribedConversationIds = conversationIds;

    await this._ensureConnection();
    if (!this.socket?.connected) return false;

    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket] subscribeConversations:', conversationIds.length, 'rooms');
    }

    return this.emit(SOCKET_EVENTS.CONVERSATION_SUBSCRIBE, { conversationIds });
  }

  async subscribeEvents(eventIds = []) {
    if (!Array.isArray(eventIds) || eventIds.length === 0) return false;

    // Store for re-subscription on reconnect
    this._subscribedEventIds = eventIds;

    await this._ensureConnection();
    if (!this.socket?.connected) return false;

    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket] subscribeEvents:', eventIds.length, 'rooms');
    }

    return this.emit(SOCKET_EVENTS.EVENT_SUBSCRIBE, { eventIds });
  }

  // ===========================================================
  // CONVERSATION ROOM MANAGEMENT
  // ===========================================================

  async joinConversation(conversationId) {
    await this._ensureConnection();
    if (!this.socket?.connected) return false;

    // Idempotent -- already in this conversation
    if (this.currentConversationId === conversationId) return true;

    // Leave previous conversation if any
    if (this.currentConversationId) {
      this.leaveConversation();
    }

    this.currentConversationId = conversationId;

    const success = this.emit(SOCKET_EVENTS.CONVERSATION_JOIN, { conversationId });
    if (!success) {
      this.currentConversationId = null;
      return false;
    }

    return true;
  }

  leaveConversation() {
    if (!this.currentConversationId) return;

    const conversationId = this.currentConversationId;
    this.currentConversationId = null;
    this.emit(SOCKET_EVENTS.CONVERSATION_LEAVE, { conversationId });
  }

  // ===========================================================
  // GROUP CHAT ROOM MANAGEMENT (simplified -- single idempotent check)
  // ===========================================================

  async joinGroupChat(groupChatId, userId) {
    await this._ensureConnection();
    if (!this.socket?.connected) return false;

    // Idempotent -- already in this group
    if (this.currentGroupChatId === groupChatId) return true;

    // Leave previous group if any
    if (this.currentGroupChatId) {
      this.leaveGroupChat();
    }

    this.currentGroupChatId = groupChatId;

    const success = this.emit(SOCKET_EVENTS.EVENT_JOIN, {
      eventId: groupChatId,
      groupChatId,
      userId,
      room: `event-${groupChatId}`,
    });

    if (!success) {
      this.currentGroupChatId = null;
      return false;
    }

    return true;
  }

  leaveGroupChat() {
    if (!this.currentGroupChatId) return;

    const groupChatId = this.currentGroupChatId;
    this.currentGroupChatId = null;
    this.emit(SOCKET_EVENTS.EVENT_LEAVE, {
      eventId: groupChatId,
      groupChatId,
      room: `event-${groupChatId}`,
    });
  }

  _rejoinActiveRooms() {
    // Re-subscribe bulk conversation rooms
    if (this._subscribedConversationIds.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket] Re-subscribing', this._subscribedConversationIds.length, 'conversation rooms');
      }
      this.emit(SOCKET_EVENTS.CONVERSATION_SUBSCRIBE, {
        conversationIds: this._subscribedConversationIds,
      });
    }

    // Re-subscribe bulk event rooms
    if (this._subscribedEventIds.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket] Re-subscribing', this._subscribedEventIds.length, 'event rooms');
      }
      this.emit(SOCKET_EVENTS.EVENT_SUBSCRIBE, {
        eventIds: this._subscribedEventIds,
      });
    }

    // Re-join active conversation
    if (this.currentConversationId) {
      this.emit(SOCKET_EVENTS.CONVERSATION_JOIN, {
        conversationId: this.currentConversationId,
      });
    }

    // Re-join active group
    if (this.currentGroupChatId) {
      this.emit(SOCKET_EVENTS.EVENT_JOIN, {
        eventId: this.currentGroupChatId,
        groupChatId: this.currentGroupChatId,
        room: `event-${this.currentGroupChatId}`,
      });
    }
  }

  // ===========================================================
  // MESSAGING
  // ===========================================================

  sendMessage(messageData) {
    if (!this.socket?.connected || !messageData) return false;

    const payload = {
      conversationId: messageData.conversation_id || messageData.conversationId,
      content: messageData.message,
      message: messageData.message,
      receiverId: messageData.receiver_id,
      senderId: messageData.sender_id,
      uuid: messageData.uuid,
      metadata: messageData.metadata || null,
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket:emit] message:send', { uuid: payload.uuid, conversationId: payload.conversationId });
    }

    return this.emit(SOCKET_EVENTS.MESSAGE_SEND, payload);
  }

  sendGroupMessage(messageData) {
    if (!this.socket?.connected || !messageData) return false;

    const eventId = messageData.eventId || messageData.groupChatId;

    const payload = {
      eventId,
      groupChatId: messageData.groupChatId,
      content: messageData.message,
      message: messageData.message,
      type: messageData.type || 'TEXT',
      metadata: messageData.metadata || null,
      uuid: messageData.uuid,
      senderId: messageData.senderId,
      sender: messageData.sender,
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket:emit] group_message:send', { uuid: payload.uuid, eventId, groupChatId: messageData.groupChatId });
    }

    return this.emit(SOCKET_EVENTS.GROUP_MESSAGE_SEND, payload);
  }

  // ===========================================================
  // TYPING INDICATORS (with 3-second auto-stop)
  // ===========================================================

  startTyping(type = 'conversation') {
    if (!this.socket?.connected) return false;

    const data = { type, isTyping: true, timestamp: Date.now() };

    if (type === 'conversation' && this.currentConversationId) {
      data.conversationId = this.currentConversationId;
    } else if (type === 'group' && this.currentGroupChatId) {
      data.groupChatId = this.currentGroupChatId;
    } else {
      return false;
    }

    const success = this.emit(SOCKET_EVENTS.START_TYPING, data);

    if (success) {
      const key = `${type}_${data.conversationId || data.groupChatId}`;
      const existingTimer = this.typingTimers.get(key);
      if (existingTimer) clearTimeout(existingTimer);

      this.typingTimers.set(key, setTimeout(() => {
        this.stopTyping(type);
      }, 3000));
    }

    return success;
  }

  stopTyping(type = 'conversation') {
    if (!this.socket?.connected) return false;

    const data = { type, isTyping: false, timestamp: Date.now() };

    if (type === 'conversation' && this.currentConversationId) {
      data.conversationId = this.currentConversationId;
    } else if (type === 'group' && this.currentGroupChatId) {
      data.groupChatId = this.currentGroupChatId;
    } else {
      return false;
    }

    const key = `${type}_${data.conversationId || data.groupChatId}`;
    const existingTimer = this.typingTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.typingTimers.delete(key);
    }

    return this.emit(SOCKET_EVENTS.START_TYPING, data);
  }

  // ===========================================================
  // UTILITY
  // ===========================================================

  emit(event, data) {
    if (!this.socket?.connected) return false;

    try {
      this.socket.emit(event, data);
      return true;
    } catch (error) {
      console.error(`Error emitting ${event}:`, error.message);
      return false;
    }
  }

  async _ensureConnection() {
    if (!this.socket?.connected) {
      await this.connect();
    }
  }

  cleanup() {
    this.typingTimers.forEach((timer) => clearTimeout(timer));
    this.typingTimers.clear();

    if (this.currentConversationId) this.leaveConversation();
    if (this.currentGroupChatId) this.leaveGroupChat();
  }

  healthCheck() {
    return {
      isConnected: this.isConnected(),
      connectionState: this.getConnectionState(),
      activeSubscriptions: Array.from(this.subscriptions.keys()),
      reconnectAttempts: this.reconnectAttempts,
      currentConversationId: this.currentConversationId,
      currentGroupChatId: this.currentGroupChatId,
    };
  }
}

export const chatSocketService = new ChatSocketService();
